import React, { useEffect, useState } from 'react';
import { useTheme } from '@material-ui/core';
import BarChartIcon from '@material-ui/icons/BarChart';
import HomeIcon from '@material-ui/icons/Home';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import WarningIcon from '@material-ui/icons/Warning';
import { CentralProviderBadgeSidebar, usePermissions, useTranslation } from '@michelin/central-provider';
import { runningOnCanvas } from '@michelin/central-provider/dist/utils/canvas';
import { auth } from 'Auth';
import { getRestApiVersion, getVersion } from 'components/Util/version';
import GLOBAL_CONFIG from 'global-variables';
import { useHistory, useLocation, useParams } from 'react-router-dom';

function Icon({ file, selected }: { file: string; selected: boolean }) {
  const theme = useTheme();
  const [svgUrl, setSvgUrl] = useState<string>('');

  const fillColor = selected ? theme.palette.text.primary : theme.palette.text.secondary;

  useEffect(() => {
    const url = `${process.env.PUBLIC_URL}/static/images/preferencesIcons/${file}.svg`;
    fetch(url)
      .then((res) => res.text())
      .then((svgText) => {
        // Replace the fill color in the SVG with the appropriate color based on selection
        const coloredSvg = svgText.replace(/(\.st1\s*\{[^}]*fill:)#[0-9a-fA-F]{3,8}/, `$1${fillColor}`);
        const encoded = encodeURIComponent(coloredSvg).replace(/'/g, '%27').replace(/"/g, '%22');
        setSvgUrl(`data:image/svg+xml;charset=UTF-8,${encoded}`);
      });
  }, [file, selected]);

  return <img src={svgUrl} alt={file} style={{ width: 26, marginRight: 5, marginLeft: -3 }} />;
}

export default function SideBar() {
  const history = useHistory();
  const location = useLocation();
  const permissions = usePermissions();
  const { t } = useTranslation();
  const { selectedAccount } = useParams<{ selectedAccount: string }>();
  const accountNumber = permissions.location?.customer_number || selectedAccount;
  const [selectedItem, setSelectedItem] = useState<string>('Home');
  const [frontendVersion, setFrontendVersion] = useState<string>('');
  const [backendVersion, setBackendVersion] = useState<string>('');
  const [restApiVersion, setRestApiVersion] = useState<string>('...');

  useEffect(() => {
    setFrontendVersion(getVersion());
    setBackendVersion(auth.getApiVersion() || '');
    getRestApiVersion()
      .then(setRestApiVersion)
      .catch(() => setRestApiVersion('...'));
  }, []);

  useEffect(() => {
    const pathname = location.pathname;
    if (pathname.includes('/tire-wheel')) {
      setSelectedItem('Tire & Wheel Preferences');
    } else if (pathname.includes('/mechanical')) {
      setSelectedItem('Mechanical Preferences');
    } else if (pathname.includes('/contacts')) {
      setSelectedItem('Contacts');
    } else if (pathname.includes('/my-admin')) {
      setSelectedItem('My Admin Area');
    } else {
      setSelectedItem('Home');
    }
  }, [location.pathname]);

  const items: CentralProviderBadgeSidebar.Item[] = [
    {
      heading: true,
      label: t('Main Menu'),
    },
    {
      label: t('Home'),
      icon: <HomeIcon />,
      selected: selectedItem === 'Home',
      onClick: () => history.push(`/${accountNumber}/home`),
      badge: 0,
    },
    {
      label: t('Request Service'),
      icon: <WarningIcon />,
      selected: selectedItem === 'Request Service',
      onClick: () => setSelectedItem('Request Service'),
      badge: 0,
    },
    {
      label: t('LiveTrack'),
      icon: <MyLocationIcon />,
      selected: selectedItem === 'LiveTrack',
      onClick: () => setSelectedItem('LiveTrack'),
      badge: 0,
    },
    {
      label: t('Fleet Business Insights'),
      icon: <BarChartIcon />,
      selected: selectedItem === 'Fleet Business Insights',
      onClick: () => GLOBAL_CONFIG.fbiUrl && window.open(GLOBAL_CONFIG.fbiUrl),
      badge: 0,
    },
    {
      heading: true,
      label: t('Preferences'),
    },
    {
      label: t('Tire & Wheel Preferences'),
      icon: <Icon file="bulk-order" selected={selectedItem === 'Tire & Wheel Preferences'} />,
      selected: selectedItem === 'Tire & Wheel Preferences',
      onClick: () => history.push(`/${accountNumber}/tire-wheel`),
      badge: 0,
    },
    {
      label: t('Mechanical Preferences'),
      icon: <Icon file="mechanical" selected={selectedItem === 'Mechanical Preferences'} />,
      selected: selectedItem === 'Mechanical Preferences',
      onClick: () => history.push(`/${accountNumber}/mechanical`),
      badge: 0,
    },
    {
      label: t('Contacts'),
      icon: <Icon file="contacts" selected={selectedItem === 'Contacts'} />,
      selected: selectedItem === 'Contacts',
      onClick: () => history.push(`/${accountNumber}/contacts`),
      badge: 0,
    },
    {
      label: t('My Admin Area'),
      icon: <Icon file="all-preferences" selected={selectedItem === 'My Admin Area'} />,
      selected: selectedItem === 'My Admin Area',
      onClick: () => history.push(`/${accountNumber}/my-admin`),
      badge: 0,
    },
  ];

  const versions = [
    {
      application: t('UI'),
      version: frontendVersion,
    },
    {
      application: t('API'),
      version: `GQL-${backendVersion} / REST-${restApiVersion}`,
    },
  ];

  return (
    <CentralProviderBadgeSidebar
      versions={versions}
      items={items}
      showCopyright={!runningOnCanvas()}
      showLegalInformation={false}
      showApplicationSelector={!runningOnCanvas()}
    />
  );
}
